import React, { useContext } from 'react';

import useSWR from 'swr';
import { Accordion, DarkModeProvider } from 'mintel-react-ui';

import useTranslate from '../../../utils/translations';
import Drawer from './Drawer';
import styles from './RelatedContentNav.scss';
import { AppShellContext, MetadataContext } from '../../../utils/contexts';
import { cwaUrl } from '../../../configuration';
import { RelatedItemsData, TrendFilters } from '../../../types';

export type RelatedContent = {
  related_items: RelatedItemsData[];
  related_observations: RelatedItemsData[];
  related_trends: RelatedItemsData[];
};

export const generateSearchUrl = (
  aggregatedInsightType: string,
  {
    contentTypes,
    filters = [],
    trends = [],
    categoryIds = [],
  }: {
    contentTypes: string[];
    filters?: TrendFilters;
    trends?: string[];
    categoryIds?: string[];
  },
) => {
  let url = `${cwaUrl}/content?contentType=${contentTypes.join(',')}`;

  if (aggregatedInsightType === 'Consumer Trend') {
    const groupedFilters = filters.reduce((acc, filter) => {
      acc[filter.filter_slug] = [
        ...(acc[filter.filter_slug] || []),
        filter.filter_id,
      ];
      return acc;
    }, {} as Record<string, number[]>);

    Object.entries(groupedFilters).forEach(([filterName, filterIds]) => {
      url += `&filters.${filterName}=${filterIds.join(',')}`;
    });
  }

  if (categoryIds.length) {
    url += `&freetext=category.id:(${categoryIds.join(' OR ')})`;
  }

  if (trends.length) {
    url += `&freetext=trends:(${trends.join(' OR ')})`;
  }

  return url;
};

const fetchRelatedContent = (
  jwt: string | undefined,
  articleId: string,
): Promise<{ data: RelatedContent }> =>
  fetch(`${cwaUrl}/api/content/related-items/${articleId}`, {
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  }).then((res) => res.json());

export const useRelatedContentData = () => {
  const { portalJwt } = useContext(AppShellContext);
  const { id } = useContext(MetadataContext);

  return useSWR([portalJwt, 'relatedContent'], () =>
    fetchRelatedContent(portalJwt, id),
  );
};

const RelatedContentNav = ({ data }: { data?: RelatedContent }) => {
  const t = useTranslate();
  const {
    id,
    trend_filters,
    leaf_category,
    aggregated_insight_type,
    trends,
  } = useContext(MetadataContext);

  if (!data) {
    return null;
  }

  return (
    <div className={styles.relatedContentNav}>
      <DarkModeProvider darkMode>
        <Accordion autoclose hideDividers darkBg initialOpened={0}>
          <Drawer
            title={t('Related observations')}
            untranslatedTitle="Related Observations"
            content={data?.related_observations}
            viewMoreLink={generateSearchUrl(aggregated_insight_type, {
              contentTypes: ['Observation'],
              trends: aggregated_insight_type === 'Observation' ? trends : [id],
            })}
          />
          <Drawer
            title={t('Related trends')}
            untranslatedTitle="Related Trends"
            content={data?.related_trends}
            viewMoreLink={generateSearchUrl(aggregated_insight_type, {
              contentTypes: ['Consumer Trend'],
              filters: trend_filters,
              trends,
            })}
          />
          <Drawer
            title={t('Recommended content')}
            untranslatedTitle="Recommended Content"
            content={data?.related_items}
            viewMoreLink={generateSearchUrl(aggregated_insight_type, {
              contentTypes: ['Report', 'Insight'],
              categoryIds: leaf_category?.id,
            })}
          />
        </Accordion>
      </DarkModeProvider>
    </div>
  );
};

export default RelatedContentNav;
